<script setup lang="ts">
import type { MemberProfile } from '~/stores/member-profile'

type ApiResponse<T> = { code: number; message: string; data: T }
type MemberProfileHint = Pick<MemberProfile, 'user_id' | 'username' | 'display_name' | 'avatar_url'>

const config = useRuntimeConfig()
const apiBase = String(config.public.apiBaseUrl || '/api/v1').replace(/\/$/, '')
const route = useRoute()
const { tokens, restore, clear, authorizationHeader, memberFetch } = useMemberAuth()
const profileStore = useMemberProfileStore()
const { profile } = storeToRefs(profileStore)
const profileHint = useCookie<MemberProfileHint | null>('goblog_member_profile_hint', { default: () => null, sameSite: 'lax', path: '/' })
const menuOpen = ref(false)
const loading = ref(true)
const loggingOut = ref(false)
let tokenTimer: ReturnType<typeof setInterval> | undefined

const visibleProfile = computed<MemberProfileHint | null>(() => profile.value || profileHint.value)
const displayName = computed(() => visibleProfile.value?.display_name || visibleProfile.value?.username || '用户')
const isLoggedIn = computed(() => Boolean(visibleProfile.value?.user_id))

function tokenExpired() {
  const token = tokens.value?.access_token
  if (!token) return true
  try {
    const payload = JSON.parse(atob(token.split('.')[1] || ''))
    return typeof payload.exp === 'number' && payload.exp * 1000 <= Date.now()
  } catch { return true }
}

function clearExpiredSession() {
  if (tokens.value?.access_token && tokenExpired()) {
    clear()
    profileStore.clearProfile()
    profileHint.value = null
    menuOpen.value = false
  }
}

async function fetchProfile() {
  const response = await memberFetch<ApiResponse<MemberProfile>>(`${apiBase}/custom_user/profile`, { timeout: 8000 })
  if (response.code !== 0 || !response.data?.user_id) throw new Error(response.message || '读取用户信息失败')
  profileStore.setProfile(response.data)
}

async function logout() {
  if (loggingOut.value) return
  loggingOut.value = true
  try {
    if (authorizationHeader()) {
      await $fetch(`${apiBase}/custom_user/auth/logout`, {
        method: 'POST', headers: { Authorization: authorizationHeader() }, timeout: 5000
      })
    }
  } catch {
    // Local token cleanup still takes precedence if a session has expired.
  } finally {
    menuOpen.value = false
    clear()
    profileStore.clearProfile()
    if (import.meta.client) window.location.reload()
  }
}

onMounted(async () => {
  restore()
  profileStore.restore()
  if (!tokens.value?.access_token) {
    profileStore.clearProfile()
    profileHint.value = null
    loading.value = false
    return
  }
  try {
    await fetchProfile()
  } catch {
    clear()
    profileStore.clearProfile()
    profileHint.value = null
  } finally {
    loading.value = false
  }
  tokenTimer = setInterval(() => { if (!tokens.value?.refresh_token) clearExpiredSession() }, 15000)
})

onBeforeUnmount(() => { if (tokenTimer) clearInterval(tokenTimer) })

watch(() => route.fullPath, () => { menuOpen.value = false })
</script>

<template>
  <div class="public-member-slot">
    <NuxtLink v-if="!isLoggedIn" to="/login" class="public-member-login grid size-9 cursor-pointer place-items-center rounded-full text-[#9fb1cc] transition-colors hover:bg-[#22d3ee]/14 hover:text-[#67e8f9]" aria-label="登录">
      <AppIcon name="user" class="size-[18px]" />
    </NuxtLink>
    <div v-else class="public-member-menu relative" :class="{ open: menuOpen }" @mouseenter="menuOpen = true" @mouseleave="menuOpen = false" @focusin="menuOpen = true" @focusout="menuOpen = false">
      <button type="button" class="public-member-trigger" :aria-expanded="menuOpen" aria-label="用户菜单" @click="menuOpen = !menuOpen">
        <img v-if="visibleProfile?.avatar_url" :src="visibleProfile.avatar_url" :alt="`${displayName} 的头像`" referrerpolicy="no-referrer">
        <span v-else class="public-member-name">{{ displayName }}</span>
        <AppIcon name="chevron-down" class="public-member-chevron" />
      </button>
      <div v-show="menuOpen" class="public-member-popover">
        <div class="public-member-summary"><strong>{{ displayName }}</strong><span>@{{ visibleProfile?.username }}</span></div>
        <NuxtLink to="/account" class="public-member-action"><AppIcon name="layout-dashboard" />个人中心</NuxtLink>
        <button type="button" class="public-member-action danger" :disabled="loggingOut" @click="logout"><AppIcon name="log-out" />{{ loggingOut ? '退出中...' : '退出登录' }}</button>
      </div>
    </div>
  </div>
</template>

<style>
.public-member-slot { display: flex; height: 36px; justify-content: flex-end; }.public-member-menu { height: 36px; }.public-member-trigger { display: flex; height: 36px; max-width: 156px; align-items: center; gap: 7px; padding: 0; border: 0; background: transparent; color: #dce9f5; cursor: pointer; font: inherit; }.public-member-trigger img { width: 32px; height: 32px; flex: 0 0 auto; border: 1px solid rgba(126, 230, 211, .36); border-radius: 50%; object-fit: cover; }.public-member-name { max-width: 104px; overflow: hidden; padding: 6px 8px; border: 1px solid rgba(116, 192, 208, .2); border-radius: 999px; background: rgba(19, 45, 66, .6); color: #b8d9e5; font-size: 12px; font-weight: 700; text-overflow: ellipsis; white-space: nowrap; }.public-member-chevron { width: 14px; flex: 0 0 auto; color: #8096ad; transition: transform .16s ease; }.public-member-menu.open .public-member-chevron { transform: rotate(180deg); }.public-member-popover { position: absolute; z-index: 60; top: 100%; right: 0; width: 210px; overflow: hidden; border: 1px solid rgba(90, 211, 190, .2); border-radius: 9px; background: #0b182a; box-shadow: 0 20px 44px rgba(0, 0, 0, .36); }.public-member-summary { display: grid; gap: 4px; padding: 14px 15px; border-bottom: 1px solid rgba(135, 167, 205, .12); background: linear-gradient(90deg, rgba(57, 190, 170, .12), transparent); }.public-member-summary strong, .public-member-summary span { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }.public-member-summary strong { color: #edf7fc; font-size: 13px; }.public-member-summary span { color: #859ab2; font-size: 11px; }.public-member-action { display: flex; width: 100%; box-sizing: border-box; align-items: center; gap: 9px; padding: 12px 15px; border: 0; background: transparent; color: #c9d8e6; cursor: pointer; font: inherit; font-size: 12px; text-align: left; text-decoration: none; }.public-member-action svg { width: 16px; color: #69dbc6; }.public-member-action:hover { background: rgba(94, 224, 200, .09); color: #a5f4e3; }.public-member-action.danger { border-top: 1px solid rgba(135, 167, 205, .1); color: #efa8b2; }.public-member-action.danger svg { color: #ef7f91; }.public-member-action.danger:hover { background: rgba(248, 113, 113, .1); color: #ffc1ca; }.public-member-action:disabled { cursor: wait; opacity: .65; }
html.theme-light .public-member-trigger { color: #303846; }
html.theme-light .public-member-trigger img { border-color: #b7d7ff; }
html.theme-light .public-member-name { border-color: #cbd8e6; background: #f5f8fc; color: #3f4854; }
html.theme-light .public-member-chevron { color: #7b8494; }
html.theme-light .public-member-popover { border-color: #dce5ef; background: #fff; box-shadow: 0 16px 36px rgba(30, 41, 59, .16); }
html.theme-light .public-member-summary { border-bottom-color: #e5eaf0; background: linear-gradient(90deg, #eef8ff, #fff); }
html.theme-light .public-member-summary strong { color: #20242b; }
html.theme-light .public-member-summary span { color: #7b8494; }
html.theme-light .public-member-action { color: #3f4854; }
html.theme-light .public-member-action svg { color: #1677a8; }
html.theme-light .public-member-action:hover { background: #eef7ff; color: #0958d9; }
html.theme-light .public-member-action.danger { border-top-color: #f0e1e3; color: #c93f55; }
html.theme-light .public-member-action.danger svg { color: #d65063; }
html.theme-light .public-member-action.danger:hover { background: #fff1f2; color: #b4233a; }
</style>
